import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@common/hooks';
import Filters from 'works/legacy/lib/filters';
import { FilteredTable } from 'works/components/filtered-table';
import { Spacing } from 'works/legacy/lib/style-helper';
import { CandidateLink } from 'works/components/candidate-link';
import { AvailabilityStateBadge } from 'works/components/availability-state-badge';
import { StaffingRequestSubmissionButton } from './staffing-request-submission-button';
import {
  EmptyContent,
  LoadingState,
  TextFilterSearch,
} from '@common/components';
import { useHasFilters } from 'works/legacy/lib/hooks/use-has-filters';

export function CandidatesList({
  loading,
  staffingRequest,
  data,
  paginationAttrs,
  handleTableChange,
}) {
  const LOCALSTORAGE_FILTER_KEY = 'CandidatesList';
  const [filters, setFilters] = useState(
    Filters.getFilters(LOCALSTORAGE_FILTER_KEY)
  );
  const STAFFING_REQUEST_OPEN = 'OPEN';
  const worksProfiles = data.records;
  const hasFilters = useHasFilters(filters);

  if (!worksProfiles) return null;

  const onFilterChange = (newFilters) => {
    setFilters(newFilters);
    Filters.setFilters(LOCALSTORAGE_FILTER_KEY, newFilters);
  };

  const columns = [
    {
      title: 'Candidate',
      dataIndex: 'id',
      key: 'fullName',
      width: 380,
      filtered: filters.fullName || false,
      filteredValue: filters.fullName || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (id, record) => {
        return (
          <CandidateLink
            firstName={record.firstName}
            lastName={record.lastName}
            link={`/works/supplier/candidates/${id}`}
          />
        );
      },
    },
    {
      title: 'Active Submissions',
      key: 'activeSubmissions',
      render: (record) => {
        return (
          <div className="active-submissions">
            {record.activeSubmissionsCount}
            <style jsx>
              {`
                .active-submissions {
                  padding-left: 50px;
                  font-size: 20px;
                }
              `}
            </style>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'availabilityStatus',
      filters: [
        {
          text: 'Available',
          value: 'AVAILABLE',
        },
        {
          text: 'On Assignment',
          value: 'ON_ASSIGNMENT',
        },
      ],
      filtered: filters.availabilityStatus || false,
      filteredValue: filters.availabilityStatus || null,
      render: (record) => {
        return (
          <AvailabilityStateBadge
            available={record.availableForDates}
            doNotRehire={record.doNotRehire}
            currentStaffingRequestAssignments={
              record.currentStaffingRequestAssignments
            }
          />
        );
      },
    },
    {
      key: 'buttonInfo',
      render: function RenderUnit(record) {
        if (staffingRequest.state !== STAFFING_REQUEST_OPEN) return;

        return (
          <StaffingRequestSubmissionButton
            staffingRequest={staffingRequest}
            selectedWorksProfile={{
              worksProfileId: record.id,
              fullName: record.fullName,
              worksSubmittalPackets: record.notArchivedWorksSubmittalPackets,
              currentStaffingRequestAssignments:
                record.currentStaffingRequestAssignments,
              profileType: record.profileType,
              identificationCode: record.identificationCode,
              supplierId: record.supplier.id,
            }}
            doNotRehire={record.doNotRehire}
            primary={record.availableForDates}
          />
        );
      },
    },
  ];

  // @TODO: remove this logic when CollectionFetcher be decoupled - WORK-6053
  // Checking worksProfile presence to hide incompatible data format while loading
  const hasFirstName = worksProfiles?.[0]?.firstName;

  if (!hasFirstName && loading) {
    return <LoadingState />;
  }

  if (!hasFilters && !loading && worksProfiles.length === 0) {
    return (
      <EmptyContent
        imagePath="./supplier/candidates/empty-candidates.png"
        header="Hmm..."
        text="There's nothing here."
      />
    );
  } else {
    return (
      <FilteredTable
        rowKey="id"
        columns={columns}
        dataSource={worksProfiles}
        loading={loading}
        pagination={paginationAttrs}
        onChange={handleTableChange}
        onFilterChange={onFilterChange}
        localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
      />
    );
  }
}

CandidatesList.fragments = {
  query: gql`
    query SupplierJobPageCollectionQuery(
      $staffingRequestId: ID!
      $limit: Int!
      $offset: Int!
      $staffingRequestStartDate: ISO8601Date!
      $staffingRequestEndDate: ISO8601Date!
      $organizationId: ID!
      $organizationBusinessId: ID!
      $organizationUnitId: ID!
      $filters: WorksProfileFiltersInput
    ) {
      paginatedRecords: paginatedWorksProfiles(
        limit: $limit
        offset: $offset
        staffingRequestId: $staffingRequestId
        filters: $filters
      ) {
        records: worksProfiles {
          id
          firstName
          lastName
          fullName
          phone
          email
          profileType
          identificationCode
          supplier {
            id
          }
          availableForDates(
            startDate: $staffingRequestStartDate
            endDate: $staffingRequestEndDate
          )
          doNotRehire(
            organizationId: $organizationId
            organizationBusinessId: $organizationBusinessId
            organizationUnitId: $organizationUnitId
          )
          currentStaffingRequestAssignments {
            id
            startDate
            endDate
            supplier {
              id
              name
              timeZone
            }
          }
          activeSubmissionsCount
          notArchivedWorksSubmittalPackets {
            id
            files {
              id
              createdAt
              filename
            }
          }
        }
        totalCount
      }
    }
  `,
};

CandidatesList.propTypes = {
  loading: PropTypes.bool,
  staffingRequest: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    canSupplierSubmitApplications: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    records: PropTypes.array,
    totalCount: PropTypes.number,
  }).isRequired,
  paginationAttrs: PropTypes.shape({
    limit: PropTypes.number,
    offset: PropTypes.number,
    staffingRequestId: PropTypes.string,
  }),
  handleTableChange: PropTypes.func,
  setSelectedKeys: PropTypes.string,
  confirm: PropTypes.func,
  clearFilters: PropTypes.func,
};
